import { FC } from 'react';

import { useTranslation } from 'react-i18next';

import { DeleteConfirmDialogProps } from './types';
import { useDeleteAccountingPeriod } from '@/api/REST/accountingPeriods';
import { ConfirmationDialog } from '@/components/global/ConfirmationDialog';

type Props = DeleteConfirmDialogProps & {
  confirmLabel?: string;
  cancelLabel?: string;
  onDeleteSuccess?: () => void;
  onDeleteError?: (error: Error) => void;
};

const DeleteConfirmDialog: FC<Props> = ({
  period,
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const { t } = useTranslation('systemAdministrationNavigation');
  const deleteMutation = useDeleteAccountingPeriod();

  const handleConfirm = async () => {
    const id = period?.accountingPeriodId ?? period?.id;
    if (!id) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(id);
      onDeleteSuccess?.();
      onConfirm?.();
      onClose();
    } catch (error) {
      onDeleteError?.(error as Error);
    }
  };

  const handleClose = () => {
    if (!deleteMutation.isPending) {
      onClose();
    }
  };

  return (
    <ConfirmationDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={
        title || t('SystemAdministrationAccountingPeriodDeleteConfirmation')
      }
      message={message}
      confirmLabel={
        confirmLabel || t('SystemAdministrationAccountingPeriodDelete')
      }
      cancelLabel={
        cancelLabel || t('SystemAdministrationAccountingPeriodCancel')
      }
      variant="warning"
      confirmLoading={deleteMutation.isPending}
      confirmDisabled={deleteMutation.isPending}
      cancelDisabled={deleteMutation.isPending}
      maxWidth="sm"
      customErrorMessage={t(
        'SystemAdministrationAccountingPeriodUnexpectedError',
      )}
      data-automation-id="AccountingPeriodDeleteConfirmationDialog"
    />
  );
};

export { DeleteConfirmDialog };
